import { Calendar, Search, X, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import {
  activePreset,
  dateInputToInstant,
  dateInputValue,
  reportPresetLabel,
  reportPresetRange,
  tt,
  WHOLE_DEFAULT_WINDOW,
  type ReportPreset,
  type ReportWindow,
} from '@agos/shared'

import { cn } from '../cn'

// ─── SearchInput ─────────────────────────────────────────────────────────────

interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  /**
   * What this box searches, for a screen reader - `SAAS.md §3.117`, defaulted by §3.119.
   *
   * **The placeholder is not a *visible* label.** It disappears the moment somebody types, so a sighted
   * operator on a half-filled box has nothing naming the field - and every one of this component's call
   * sites was unlabelled until the Tower's own hand-rolled inputs, which **did** carry an `aria-label`,
   * were converted onto it. Taking a component in should not cost a screen its accessibility.
   *
   * **It is, however, a perfectly good accessible name, and that is §3.119.** An `aria-label` is read
   * from the accessibility tree rather than from the pixels, so it never disappears - and these
   * placeholders already say exactly what the box searches: *Hľadať meno, e-mail, telefón alebo
   * pozíciu...*. So an absent `label` falls back to the placeholder, which named **26 `FilterBar`
   * searches across the manager** in one line, none of which could carry a label at all. The two
   * statements are consistent: not a visible label, and a better accessible name than none.
   */
  label?: string
  /**
   * The marker a spec finds this box by.
   *
   * The Tower's hand-rolled boxes carried `data-franchise-search` and `data-module-search`, which
   * `tower.spec.ts` fills by name. A shared component with no way to carry one would have forced either
   * a wrapper `<div>` or a rewritten spec, and the marker is the more stable of the two.
   */
  testId?: string
}

export function SearchInput({
  value,
  onChange,
  placeholder,
  className,
  label,
  testId,
}: SearchInputProps) {
  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        // §3.119 - the placeholder as the fallback accessible name. `aria-label` wins over the
        // placeholder for a screen reader either way, so passing one is never worse than not.
        aria-label={label ?? placeholder ?? tt('Hľadať...')}
        {...(testId === undefined ? {} : { [testId]: true })}
        // FB-006: legacy's default reads "Hladat..." — the diacritics were
        // stripped. sk.json spells it "Hľadať".
        placeholder={placeholder ?? tt('Hľadať...')}
        className="w-full pl-9 pr-8 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={tt('Vymazať')}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )
}

// ─── FilterSelect / FilterBar ────────────────────────────────────────────────

interface FilterOption {
  value: string
  label: string
  count?: number
}

interface FilterSelectProps {
  value: string
  onChange: (value: string) => void
  options: FilterOption[]
  className?: string
  /** What this select filters. Legacy ships them unlabelled; a screen reader reads
   *  only the current option, which on a filter bar of three says nothing. */
  label?: string
  /**
   * Draw the `label` above the control — §9.192.
   *
   * **Opt-in, with 34 call sites.** Nearly all of them sit in a horizontal `FilterBar` where a
   * caption above each would reflow a row nobody has reviewed; the screens that need it are the ones
   * laying filters out in a grid. The reasoning behind `label` was right and stopped one step short:
   * a sighted operator also reads only the current option, so a panel of six selects all showing
   * *Všetci* says nothing about what any of them filters.
   */
  showLabel?: boolean
  /**
   * The marker a spec finds this select by - `SAAS.md §3.117`.
   *
   * Two Tower filters carried `data-franchise-picker`, which `tower.spec.ts` selects on six times. A
   * shared component with no way to carry one forces either a wrapper element or a rewritten spec, and
   * the marker is the more stable of the two. Same shape as `SearchInput.testId`.
   */
  testId?: string
}

export function FilterSelect({
  value,
  onChange,
  options,
  className,
  label,
  showLabel = false,
  testId,
}: FilterSelectProps) {
  const select = (
    <select
      value={value}
      aria-label={label}
      {...(testId === undefined ? {} : { [testId]: true })}
      onChange={(event) => onChange(event.target.value)}
      className={cn(
        'w-full sm:w-auto px-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all',
        className,
      )}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
          {option.count != null ? ` (${option.count})` : ''}
        </option>
      ))}
    </select>
  )

  if (!showLabel || label === undefined) return select

  return (
    <label className="block space-y-1">
      <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {select}
    </label>
  )
}

interface FilterBarProps {
  search?: { value: string; onChange: (value: string) => void; placeholder?: string }
  filters?: ReactNode
  actions?: ReactNode
  className?: string
}

export function FilterBar({ search, filters, actions, className }: FilterBarProps) {
  return (
    <div
      className={cn(
        'flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3 mb-4',
        className,
      )}
    >
      {search && (
        <SearchInput
          value={search.value}
          onChange={search.onChange}
          placeholder={search.placeholder}
          className="w-full sm:w-64"
        />
      )}
      {filters}
      {actions && <div className="flex items-center gap-2 flex-wrap sm:ml-auto">{actions}</div>}
    </div>
  )
}

// ─── DateRangePicker ─────────────────────────────────────────────────────────

interface DateRangePickerProps {
  from: string
  to: string
  onFromChange: (value: string) => void
  onToChange: (value: string) => void
  className?: string
}

export function DateRangePicker({
  from,
  to,
  onFromChange,
  onToChange,
  className,
}: DateRangePickerProps) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="relative">
        <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="date"
          value={from}
          aria-label={tt('Od')}
          onChange={(event) => onFromChange(event.target.value)}
          className="pl-8 pr-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all"
        />
      </div>
      <span className="text-xs text-muted-foreground">-</span>
      <div className="relative">
        <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          type="date"
          value={to}
          aria-label={tt('Do')}
          onChange={(event) => onToChange(event.target.value)}
          className="pl-8 pr-3 py-2 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all"
        />
      </div>
    </div>
  )
}

/**
 * How a period button looks, chosen or not - `SAAS.md §3.104`.
 *
 * **One string, two controls.** `ButtonGroup` below and `ReportWindowBar` further down are both rows
 * of period buttons, they sat forty lines apart with different corners, grounds and transitions, and
 * §3.5 decides which wins: *"the design is the manager's and the customer's."* `ButtonGroup` is the
 * manager's - Statistics, Reports, Revenue, Ops, Products - so its look is the one kept, and the
 * Tower's three reports adopt it.
 *
 * **Deliberately not a shared component.** They are different controls: one is a preset row with a
 * date picker and a reset beside it, the other a generic segmented choice. Unifying the components
 * would give one of them parts that do nothing (§3.98's first draft tried a third component and was
 * reverted). What they share is the appearance, so the appearance is what is extracted.
 */
function periodButtonClass(chosen: boolean, size: 'sm' | 'md' = 'sm'): string {
  return cn(
    'flex items-center gap-1.5 rounded-xl font-bold border transition-all',
    size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3.5 py-2 text-sm',
    chosen
      ? 'bg-primary text-primary-foreground border-primary'
      : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/30',
  )
}

// ─── ButtonGroup ─────────────────────────────────────────────────────────────

interface ButtonGroupOption {
  value: string
  label: string
  icon?: LucideIcon
  count?: number
}

interface ButtonGroupProps {
  options: ButtonGroupOption[]
  value: string
  onChange: (value: string) => void
  className?: string
  size?: 'sm' | 'md'
  /**
   * An attribute name each button carries, valued with its own option - `SAAS.md §3.117`.
   *
   * `testIdKey="data-module-filter"` renders `data-module-filter="billing"` on the billing chip, which
   * is exactly what the Tower's hand-rolled chips carried and what `tower.spec.ts` selects by. The same
   * shape as `DataTable`'s `rowAttributes`, and for the same reason: taking a shared component in must
   * not cost a screen the markers its specs already name.
   */
  testIdKey?: string
}

export function ButtonGroup({
  options,
  value,
  onChange,
  className,
  size = 'sm',
  testIdKey,
}: ButtonGroupProps) {
  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {options.map((option) => {
        const isActive = value === option.value
        const Icon = option.icon
        return (
          <button
            key={option.value}
            type="button"
            // Which option is chosen was carried by colour alone, so a screen reader read seven
            // identical buttons and a test could not tell the selected period from the rest.
            aria-pressed={isActive}
            {...(testIdKey === undefined ? {} : { [testIdKey]: option.value })}
            onClick={() => onChange(option.value)}
            className={periodButtonClass(isActive, size)}
          >
            {Icon && <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />}
            {option.label}
            {option.count != null && (
              <span
                className={cn(
                  'px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none',
                  isActive
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {option.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ─── TabGroup ────────────────────────────────────────────────────────────────

interface Tab {
  id: string
  label: string
  icon?: LucideIcon
  count?: number
}

interface TabGroupProps {
  tabs: Tab[]
  active: string
  onChange: (id: string) => void
  className?: string
  /** `pills` = rounded buttons; `bar` = the underlined section tabs. */
  variant?: 'pills' | 'bar'
}

export function TabGroup({ tabs, active, onChange, className, variant = 'pills' }: TabGroupProps) {
  const isBar = variant === 'bar'

  return (
    <div
      className={cn(
        isBar
          ? 'flex flex-nowrap sm:flex-wrap gap-0 border-b border-border mb-4 overflow-x-auto'
          : 'flex flex-wrap gap-1.5 mb-4',
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = active === tab.id
        const Icon = tab.icon
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id)}
            className={cn(
              isBar
                ? 'flex items-center gap-2 px-3 sm:px-5 py-2.5 sm:py-3 text-sm font-semibold transition-all relative shrink-0 sm:shrink whitespace-nowrap'
                : 'flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all',
              isBar
                ? isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
                : isActive
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/30',
            )}
          >
            {Icon && <Icon className={isBar ? 'w-4 h-4' : 'w-3.5 h-3.5'} />}
            {tab.label}
            {tab.count != null && (
              <span
                className={cn(
                  'px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none',
                  isBar ? '' : 'ml-0.5',
                  isActive
                    ? isBar
                      ? 'bg-primary/15 text-primary'
                      : 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-muted text-muted-foreground',
                )}
              >
                {tab.count}
              </span>
            )}
            {isBar && isActive && (
              <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full" />
            )}
          </button>
        )
      })}
    </div>
  )
}

// ─── ReportWindowBar ─────────────────────────────────────────────────────────

/**
 * The period a Control Tower report is asked about - `SAAS.md §3.84`.
 *
 * **One bar, four screens, and each brings its own preset row.** `/profitability`,
 * `/franchises/:id/usage`, `/cardpay` and `/billing` all rendered a fixed window while the endpoints
 * behind them had accepted `from` and `to` all along. Legacy gives each of those pages a date range
 * and presets, and its three preset lists differ on purpose - `presets` is therefore a parameter
 * rather than a constant here.
 *
 * **The reset is not decoration.** With no window chosen the screen sends neither parameter and the
 * server picks its own default, which is exactly what these reports show today; without a way back
 * to that state, opening a preset once would leave the operator unable to see the figure they are
 * used to. That is also what keeps the change additive.
 */
interface ReportWindowBarProps {
  window: ReportWindow
  onChange: (window: ReportWindow) => void
  presets: readonly ReportPreset[]
  /** What the server chose, when nothing is selected - shown so the figure is never unlabelled. */
  defaultNote?: string
  className?: string
}

export function ReportWindowBar({
  window: chosen,
  onChange,
  presets,
  defaultNote,
  className,
}: ReportWindowBarProps) {
  const active = activePreset(chosen, presets)
  const custom = chosen.from !== '' && active === null

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)} data-report-window>
      <div className="flex flex-wrap gap-1.5">
        {presets.map((preset) => (
          <button
            key={preset}
            type="button"
            data-report-preset={preset}
            data-active={active === preset ? 'true' : 'false'}
            onClick={() => onChange(reportPresetRange(preset))}
            // §3.104 - the manager's look, not this bar's own. `data-report-preset` and
            // `data-active` are unchanged: `tower.spec.ts` reads both.
            className={periodButtonClass(active === preset)}
          >
            {reportPresetLabel(preset)}
          </button>
        ))}
      </div>

      <DateRangePicker
        from={dateInputValue(chosen.from)}
        to={dateInputValue(chosen.to)}
        onFromChange={(value) =>
          onChange({ ...chosen, from: dateInputToInstant(value, 'from') })
        }
        onToChange={(value) => onChange({ ...chosen, to: dateInputToInstant(value, 'to') })}
      />

      {chosen.from === '' ? (
        defaultNote === undefined ? null : (
          <span className="text-xs text-muted-foreground" data-report-window-default>
            {defaultNote}
          </span>
        )
      ) : (
        <button
          type="button"
          data-report-window-reset
          onClick={() => onChange(WHOLE_DEFAULT_WINDOW)}
          // §3.104 - the same class as the presets it stands beside. It hand-wrote the old
          // unchosen look, so giving the presets the manager's and leaving this one would have
          // put two shapes of button in one row - an inconsistency the unification introduced.
          className={periodButtonClass(false)}
        >
          {tt('Predvolené')}
        </button>
      )}

      {custom && (
        <span className="text-xs text-muted-foreground" data-report-window-custom>
          {tt('Vlastný rozsah')}
        </span>
      )}
    </div>
  )
}
